/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component, Injector, Input } from '@angular/core';
import { type ValidationErrors, type ValidatorFn, Validators } from '@angular/forms';
import {
type Command, type PromptConfigFull,
DMValidators, FormControl,
FormGroup, FormSlideWindowAbstract } from '@dm/core';
import { ToolService } from 'app/common/services';
import { ServiceNis } from 'app/common/types/mo';
import { SWITCH } from 'app/config';

import { ServiceNisService } from '../service-nis.service';
import { ServiceNisStrService } from '../service-nis-str.service';

@Component({
    selector: 'app-configure-service-nis',
    templateUrl: './configure-service-nis.component.html',
    styleUrls: ['./configure-service-nis.component.scss'],
})
export class ConfigureServiceNisComponent extends FormSlideWindowAbstract implements OnInit {
    /**
     * 是否展示极简样式
     */
    @Input() simpleModeStyle = false;
    @Input() data: ServiceNis;
    @Input() vstoreId: string;

    i18n = this.str.i18n;

    pageReady = true;
    windowVisible = true;

    loading = false;
    loadFailed = false;

    vm: FormGroup;
    errorMessage: any;
    SWITCH = SWITCH;

    isSimpleMode: boolean;

    // IP或域名校验器
    ipOrDomainValidator: ValidatorFn = DMValidators.or([DMValidators.ipv4, DMValidators.strictDomain]);

    constructor(
        injector: Injector,
        public str: ServiceNisStrService,
        private service: ServiceNisService,
        private toolService: ToolService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        this.isSimpleMode = SWITCH.IS_SIMPLE_MODE || this.simpleModeStyle;
        this.getDataAndInit();
    }

    refresh(): void {
        this.getDataAndInit();
    }

    getDataAndInit(): void {
        if (this.data) {
            this.initVm();
            return;
        }

        this.loading = true;
        this.loadFailed = false;
        this.service.get(this.vstoreId)
            .subscribe(({ data }) => {
                this.data = data;
                this.initVm();

                this.loading = false;
                this.loadFailed = false;
            }, () => {
                this.loading = false;
                this.loadFailed = true;
        });
    }

    /**
     * 初始化表单
     */
    initVm(): void {
        const data = this.data;
        const i18n = this.i18n;

        this.vm = new FormGroup({
            NISDOMAIN: new FormControl(data.NISDOMAIN || '', [
                Validators.required,
                this.domainValidator,
            ]),
            primaryIp: new FormControl(data.primaryIp || '', [
                Validators.required,
                DMValidators.rangeByteLength(1, 255),
                this.ipOrDomainValidator,
            ]),
            standbyIp1: new FormControl(data.standbyIp1 || '', [
                DMValidators.rangeByteLength(0, 255),
                this.ipOrDomainValidator,
            ]),
            standbyIp2: new FormControl(data.standbyIp2 || '', [
                DMValidators.rangeByteLength(0, 255),
                this.ipOrDomainValidator,
            ]),
        }, {
            validators: [
                DMValidators.repeat(['primaryIp', 'standbyIp1', 'standbyIp2']),
            ],
            updateOn: 'blur',
        });

        this.errorMessage = {
            NISDOMAIN: {
                required: this.toolService.requiredErr(i18n.domainLab),
                domain: i18n.domainErr,
            },
            primaryIp: {
                required: this.toolService.requiredErr(i18n.primaryIpAddressLab),
                rangeByteLength: this.toolService.rangeByteLengthErr(1, 255),
                ipv4: i18n.ipErr,
                repeat: i18n.ipRepeatErr,
            },
            standbyIp1: {
                rangeByteLength: this.toolService.rangeByteLengthErr(0, 255),
                ipv4: i18n.ipErr,
                repeat: i18n.ipRepeatErr,
            },
            standbyIp2: {
                rangeByteLength: this.toolService.rangeByteLengthErr(0, 255),
                ipv4: i18n.ipErr,
                repeat: i18n.ipRepeatErr,
            },
        };
    }

    /**
     * 域名校验器。
     * 1、长度范围为1～63个字符，由字母、数字、“_”、“-”组成，不能以“_”或“-”开头和结尾。
     * 2、可以包含多级子域名，各级子域名之间用“.”隔开，“.”不能位于开头和结尾。
     */
    domainValidator = (control: FormControl): ValidationErrors | null => {
        const domain: string = control.value;

        if (!domain) {
            return null;
        }

        // 验证格式是否正确
        const reg = /^([a-zA-Z0-9\-\_\.]){0,63}$/;
        if (!reg.test(domain)) {
            return { domain: true };
        }

        let valid = true;

        // 不能以以下字符开头或结尾
        const invalidLetters = ['_', '-', '.'];
        if (invalidLetters.includes(domain[0]) || invalidLetters.includes(domain.slice(-1))) {
            valid = false;
        }

        // 不能包含连续的..
        if (domain.includes('..')) {
            valid = false;
        }

        return valid ? null : { domain: true };
    };

    /**
     * 测试服务器地址
     */
    test(name: string): void {
        const control = this.vm.get(name) as FormControl;

        control.validate((valid) => {
            if (!valid) {
                return;
            }

            this.service.test(this.data.vstoreId, control.value, {
                mask: true,
                doSuccess: true,
                restNameParams: [control.value],
            }).subscribe();
        });
    }

    /**
     * 高危提示
     */
    prompt(): PromptConfigFull[] {
        const prompts: PromptConfigFull[] = [
            {
                id: 'prompt-service-nis-configureMsg',
                opType: 'warning',
                title: this.i18n.configureTitle,
                content: this.i18n.configureMsg,
            },
        ];

        return prompts;
    }

    /**
     * 构建下发命令
     */
    buildCmds(): Command[] {
        const cmds: Command[] = [];
        const params: ServiceNis = this.vm.value;
        const ips: string[] = [params.primaryIp];

        if (params.standbyIp1) {
            ips.push(params.standbyIp1);
        }
        if (params.standbyIp2) {
            ips.push(params.standbyIp2);
        }
        params.NISSERVERADDR = JSON.stringify(ips);

        delete params.primaryIp;
        delete params.standbyIp1;
        delete params.standbyIp2;

        cmds.push({
            name: this.i18n.modifyResult,
            action: this.service.modify(this.data.vstoreId, params),
        });

        return cmds;
    }
}
